import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import { deleteGsa, getAllGsas, getGsaList } from '../../../../api/endpoints/system';
import type { Gsa } from '../../../../api/types/system';
import { useAuthStore } from '../../../../auth/authStore';
import { ApiErrorAlert } from '../../../../components/ApiErrorAlert';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { ListSearchField } from '../../../../components/ListSearchField';
import { GsaFormDialog } from '../GsaFormDialog';

/** List rendered as a plain MUI Table (not @mui/x-data-grid — incompatible with this project's
 *  pinned TypeScript 4.9.5). Full CRUD: create + edit (`GsaFormDialog`), delete
 *  (`Gsa/DeleteGsa`, keyed off `id`), logo upload on edit. */
export default function GsaListPage() {
  const [formGsa, setFormGsa] = useState<Gsa | null | undefined>(undefined);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Gsa | null>(null);
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isMasterGsa = useAuthStore((s) => !!s.currentUser?.isMasterGsa);

  const query = useQuery({
    queryKey: ['system', 'gsaList', showAll ? 'all' : 'own'],
    queryFn: () => (showAll ? getAllGsas() : getGsaList()),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteGsa({ id: deleteTarget!.id, name: deleteTarget!.name }),
    onSuccess: () => {
      enqueueSnackbar('Firma silindi.', { variant: 'success' });
      setDeleteTarget(null);
      queryClient.invalidateQueries({ queryKey: ['system', 'gsaList'] });
    },
    onError: (err) =>
      enqueueSnackbar(err instanceof Error ? err.message : 'Silme başarısız.', { variant: 'error' }),
  });
  const rows: Gsa[] = useMemo(() => query.data?.gsaList ?? [], [query.data]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('tr');
    if (!term) return rows;
    return rows.filter((row) =>
      [row.name, row.gsaType, row.city, row.email, row.taxNo, row.isActive ? 'Aktif' : 'Pasif']
        .filter((v) => v !== undefined && v !== null && v !== '')
        .join(' ')
        .toLocaleLowerCase('tr')
        .includes(term)
    );
  }, [rows, search]);

  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Firmalar (Acenteler)</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setFormGsa(null);
            setFormOpen(true);
          }}
        >
          Yeni Firma
        </Button>
      </Stack>

      <Stack direction="row" sx={{ mb: 2, alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <ListSearchField value={search} onChange={setSearch} placeholder="Firma, şehir, e-posta, vergi no..." />
        {isMasterGsa && (
          <FormControlLabel
            control={<Switch checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />}
            label="Tüm firmalar"
          />
        )}
      </Stack>

      {query.isError && <ApiErrorAlert error={query.error} sx={{ mb: 2 }} />}
      {query.isFetching && <CircularProgress size={24} sx={{ mb: 2 }} />}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Firma Adı</TableCell>
              <TableCell>Tip</TableCell>
              <TableCell>Şehir</TableCell>
              <TableCell>E-posta</TableCell>
              <TableCell>Vergi No</TableCell>
              <TableCell>Durum</TableCell>
              <TableCell align="right">İşlem</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.length === 0 && !query.isFetching && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    {rows.length === 0 ? 'Kayıtlı firma bulunamadı.' : 'Aramayla eşleşen firma bulunamadı.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {filteredRows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>{row.name}</TableCell>
                <TableCell>{row.gsaType ?? '—'}</TableCell>
                <TableCell>{row.city ?? '—'}</TableCell>
                <TableCell>{row.email ?? '—'}</TableCell>
                <TableCell>{row.taxNo ?? '—'}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={row.isActive ? 'Aktif' : 'Pasif'}
                    color={row.isActive ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setFormGsa(row);
                      setFormOpen(true);
                    }}
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => setDeleteTarget(row)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <GsaFormDialog open={formOpen} gsa={formGsa} onClose={() => setFormOpen(false)} />
      <ConfirmDialog
        open={!!deleteTarget}
        title="Firmayı sil"
        description={`${deleteTarget?.name ?? ''} firmasını silmek istediğinize emin misiniz?`}
        confirmLabel="Sil"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate()}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
}
